import qs from 'qs';
import sha1 from 'sha1';
import getConfig from 'next/config';
import styled from 'styled-components';
import { useTranslations } from 'ilenia';
import { useCallback, useEffect, useState } from 'react';

import { getUTMCookies } from '@bscom/tracking';
import { trackFormSubmitted } from '@bscom/tracking';
import { SpinnerLoader } from '@bscom/components/Loading';
import { domainWithProtocolRegex } from '@bscom/utilities/regex';

import { getLastReferrer } from '../lib/tracking';

const { publicRuntimeConfig } = getConfig();

export const ExternalSignupForm = ({
  background,
  newBrand = false,
  hideDisclaimer = false,
}: {
  background?: any;
  newBrand?: boolean;
  hideDisclaimer?: boolean;
}) => {
  const [, localeCode] = useTranslations();
  const [frameHeight, setFrameHeight] = useState(569);
  const [iframeUrl, setIframeUrl] = useState('');
  const [frameVisible, setFrameVisible] = useState(true);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [signupFormSuccessHandled, setSignupFormSuccessHandled] =
    useState(false);

  const checkTrackingOptions = () => {
    const consentGiven = window.OptanonActiveGroups?.includes('C0002') === true;
    setTrackingEnabled(consentGiven);
  };

  const getIframeUrl = useCallback(() => {
    const {
      website,
      domain: queryDomain,
      ...queryParameters
    } = qs.parse(window?.location.search.substring(1));
    const domain = queryDomain || website;
    const url = window?.location.toString();
    const queryParams = {
      locale: localeCode,
      url,
      newBrand,
      hideDisclaimer,
      lastReferrer: getLastReferrer(),
      ...getUTMCookies(),
      ...queryParameters,
      ...(domain ? { domain: domain, domainLocked: true } : {}),
      ...(background ? { background } : {}),
      tracking: trackingEnabled,
    };

    return `${
      publicRuntimeConfig.externalSignupFormBaseUrl
    }/form?${qs.stringify(queryParams, { indices: false })}`;
  }, [background, trackingEnabled]);

  const onMessageFromSignupForm = useCallback(
    (e) => {
      if (!e.data) {
        return;
      }

      if (e.data.name === 'formDimensions') {
        setFrameHeight(e.data.dimensions.height);
      } else if (
        e.data.name === 'signup-success' &&
        !signupFormSuccessHandled
      ) {
        setSignupFormSuccessHandled(true);

        const { email, redirectUrl, demo, country, website, company } = e.data;

        if (redirectUrl) {
          setFrameVisible(false);
        }

        if (email) {
          window.analytics?.identify(
            sha1(`${email.toLowerCase()}TrustpilotTackingHasherIsCool`),
            {}
          );
          trackFormSubmitted({
            country,
            form: 'Free signup',
            label: 'Free signup',
            demo,
            lastReferrer: getLastReferrer(),
            emailMatchesWebsite: doesEmailMatchWebsite(email, website),
            website,
            company,
            email,
          });

          if (redirectUrl) {
            window.location.assign(redirectUrl);
          }
        } else if (redirectUrl) {
          window.location.assign(redirectUrl);
        }
      }
    },
    [signupFormSuccessHandled]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', onMessageFromSignupForm, false);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('message', onMessageFromSignupForm, false);
      }
    };
  }, [onMessageFromSignupForm]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIframeUrl(getIframeUrl());
    }
  }, [localeCode, trackingEnabled, getIframeUrl]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.OneTrust?.OnConsentChanged(checkTrackingOptions);
      checkTrackingOptions();
    }
  }, []);

  return (
    <FormContainer>
      <LoaderContainer>
        <SpinnerLoader />
      </LoaderContainer>
      <FrameContainer visible={frameVisible}>
        <iframe
          id="signup-form-frame"
          src={iframeUrl}
          style={{
            width: '100%',
            border: 'none',
            overflow: 'hidden',
            height: `${frameHeight}px`,
          }}
        />
      </FrameContainer>
    </FormContainer>
  );
};

const doesEmailMatchWebsite = (email: string, website: string) => {
  if (!email || !website) {
    return false;
  }

  const match = domainWithProtocolRegex.exec(website);

  if (!match || match.length !== 4) {
    return false;
  }

  return email.trim().toLowerCase().endsWith(`${match[2]}.${match[3]}`);
};

const FormContainer = styled.div`
  position: relative;
`;

const FrameContainer = styled.div<{ visible?: boolean }>(({ visible }) => ({
  position: 'relative',
  zIndex: 2,
  visibility: visible ? 'visible' : 'hidden',
}));

const LoaderContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

export default ExternalSignupForm;
